import { FC } from 'react'

import { useHistory } from 'react-router-dom'
import { styled } from 'styled-components'

import { Pagination } from 'peach/components'

import { Clickable } from 'core/components'
import { R } from 'core/helpers'

type ItemProps = {
  id?: string
  title: string
  subtitle?: string
  note?: string
  to?: string
  onClick: () => void
}

const Item: FC<ItemProps> = ({ id, title, subtitle, note, to, onClick }) => {
  const history = useHistory()

  const handleClick = () => {
    onClick() // Execute the passed onClick (closes dialog, etc.)

    // Navigate after dialog has time to close (onDone has 100ms delay, so we wait 200ms)
    if (to) {
      setTimeout(() => {
        history.push(to)
      }, 200)
    }
  }

  return (
    <ItemContainer onClick={handleClick} variant='link'>
      <ItemTitle>{title}</ItemTitle>
      {subtitle && (
        <ItemSubtitle>
          {subtitle}: {id}
        </ItemSubtitle>
      )}
      {note && <ItemNote>{note}</ItemNote>}
    </ItemContainer>
  )
}

type GroupProps = {
  list: Array<any>
  onClick: (item: any) => void
}

const Group: FC<GroupProps> = ({ list, onClick }) =>
  R.map(list, (item = {}) => {
    const { id, to, label, subtitle, note } = item

    return (
      <Item
        id={id}
        key={`${id!} - ${to}`} // The id should always be present
        note={note}
        onClick={() => onClick(item)}
        subtitle={subtitle}
        title={label || 'Untitled'}
        to={to}
      />
    )
  })

type SearchResultsProps = {
  results: Array<any>
  onClick: (item: any) => void
  // Pagination props for employee search
  queryType?: 'borrower' | 'employee'
  total?: number
  limit?: number
  offset?: number
  onPaginationChange?: (offset: number) => void
}

const SearchResults: FC<SearchResultsProps> = ({
  results,
  onClick,
  queryType,
  total,
  limit = 25,
  offset = 0,
  onPaginationChange,
}) => {
  const handlePaginationChange = (newOffset: number) => {
    onPaginationChange?.(newOffset)
  }

  const showPagination = queryType === 'employee' && !!total && total > limit

  return (
    <SearchResultsContainer>
      <ResultsScrollArea>
        {R.pipe(
          results ?? [],
          R.groupBy(R.prop('group')),
          R.toPairs,
          R.map(([key, group]) => {
            if (!R.isEmpty(group)) {
              return <Group key={key} list={group} onClick={onClick} />
            }
            return null
          }),
          R.filter(R.isTruthy),
        )}
      </ResultsScrollArea>

      {showPagination && (
        <PaginationContainer>
          <Pagination
            limit={limit}
            maxPagesVisible={5}
            offset={offset}
            onChange={handlePaginationChange}
            total={total}
          />
        </PaginationContainer>
      )}
    </SearchResultsContainer>
  )
}

export default SearchResults

const SearchResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 200px;
  max-height: 440px;
`

const ResultsScrollArea = styled.div`
  flex: 1;
  padding-right: 4px; /* Space for scrollbar */
  overflow: hidden auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 3px;
    background: ${({ theme }) => theme.background};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background: ${({ theme }) => theme.border};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.mutedText};
  }
`

const PaginationContainer = styled.div`
  display: flex;
  flex-shrink: 0; /* Prevent pagination from shrinking */
  justify-content: center;
  border-top: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.background};
  padding: 16px 8px;
`

const ItemContainer = styled(Clickable)`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  border-radius: 4px;
  padding: 16px;
  width: 100%;
  text-decoration: none;

  &:hover {
    background-color: ${({ theme }) => theme.hover};
  }
`

const ItemTitle = styled.div`
  color: ${({ theme }) => theme.linkText};
  font-size: 14px;
  font-weight: bold;
`

const ItemSubtitle = styled.div`
  padding-top: 4px;
  color: ${({ theme }) => theme.mutedText};
  font-size: 12px;
`

const ItemNote = styled.div`
  padding-top: 4px;
  color: ${({ theme }) => theme.mutedText};
  font-size: 12px;
`
